export interface VideoEmbedConfig {
  autoplay?: boolean;
  enablejsapi?: boolean;
}

export function convertVideoUrlToEmbed(
  url: string,
  config?: VideoEmbedConfig
): string {
  const { autoplay, enablejsapi } = config ?? {};

  if (url?.includes("vimeo")) {
    const match = url.match(/vimeo\.com\/(\d+)/);

    if (match) {
      const params = new URLSearchParams();
      params.set("z", "0");
      if (autoplay) params.set("autoplay", "1");

      return `https://player.vimeo.com/video/${match[1]}?${params.toString()}`;
    }

    return url;
  }

  // Added support for various YouTube URL formats to make it more robust:
  // - youtube.com/watch?v=VIDEO_ID
  // - youtube.com/watch?feature=share&v=VIDEO_ID
  // - youtu.be/VIDEO_ID
  // - youtube.com/embed/VIDEO_ID
  // - youtube.com/v/VIDEO_ID
  // - youtube.com/shorts/VIDEO_ID
  const patterns = [
    /[?&]v=([^&\s]+)/,                    // watch?v= or &v=
    /youtu\.be\/([^?&\s]+)/,              // youtu.be/
    /youtube\.com\/embed\/([^?&\s]+)/,    // /embed/
    /youtube\.com\/v\/([^?&\s]+)/,        // /v/
    /youtube\.com\/shorts\/([^?&\s]+)/,   // /shorts/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      const params = new URLSearchParams();
      params.set("modestbranding", "1");
      if (autoplay) params.set("autoplay", "1");
      if (enablejsapi) params.set("enablejsapi", "1");

      return `https://www.youtube-nocookie.com/embed/${match[1]}?${params.toString()}`;
    }
  }

  return url;
}

export type YouTubeThumbnailQuality =
  | "default"
  | "mqdefault"
  | "hqdefault"
  | "sddefault"
  | "maxresdefault";

export interface VideoThumbnailResult {
  url: string | null;
  provider: "youtube" | "vimeo" | "unknown";
  videoId: string | null;
}

/**
 * Extracts video thumbnail URL from YouTube or Vimeo video URLs.
 *
 * For YouTube: Returns a direct thumbnail URL (no API call needed).
 * For Vimeo: Returns the oEmbed API URL that must be fetched to get the thumbnail.
 *            Fetch this URL and parse the JSON response for the `thumbnail_url` field.
 */
export function getVideoThumbnail(
  url: string,
  quality: YouTubeThumbnailQuality = "hqdefault"
): VideoThumbnailResult {
  if (!url) {
    return { url: null, provider: "unknown", videoId: null };
  }

  if (url.includes("vimeo")) {
    const match = url.match(/vimeo\.com\/(\d+)/);

    if (match) {
      const videoId = match[1];
      return {
        url: `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=1280`,
        provider: "vimeo",
        videoId,
      };
    }

    return { url: null, provider: "vimeo", videoId: null };
  }

  // Support various YouTube URL formats
  const patterns = [
    /[?&]v=([^&\s]+)/,                    // watch?v= or &v=
    /youtu\.be\/([^?&\s]+)/,              // youtu.be/
    /youtube\.com\/embed\/([^?&\s]+)/,    // /embed/
    /youtube\.com\/v\/([^?&\s]+)/,        // /v/
    /youtube\.com\/shorts\/([^?&\s]+)/,   // /shorts/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      const videoId = match[1];
      return {
        url: `https://img.youtube.com/vi/${videoId}/${quality}.jpg`,
        provider: "youtube",
        videoId,
      };
    }
  }

  return { url: null, provider: "unknown", videoId: null };
}

export const validUrlProtocols = [
  "http://",
  "https://",
  "mailto:",
  "tel:",
  "sms:",
  "whatsapp://",
  "telegram:",
  "skype:",
  "facetime:",
  "facetime-audio:",
  "maps:",
  "geo:",
];

export function hasValidUrlProtocol(url: string | undefined): boolean {
  if (!url) return false;
  return validUrlProtocols.some((protocol) => url.startsWith(protocol));
}
